import React, { useEffect, useState } from 'react'
import {
  Checkbox,
  IconButton,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import { AlmostThereComponentProps, AlmostThereComponentState } from './types'
import { ENTER_PROMO, GET_PHYSICAL, SAFE_AND_SECURE } from '../types'
import {
  isInvalidAddress,
  isSsnError
} from '../../../services/formhelper.service'

export const AlmostThereComponent: React.FC<AlmostThereComponentProps> = ({
  formData,
  navigateStep,
  setFormData,
  error
}) => {
  const [values, setValues] = useState<AlmostThereComponentState>({
    ...formData,
    validationErrors: {}
  })

  useEffect(() => {
    setFormData({ ...values })
  }, [values])

  useEffect(() => {
    if (values.kycConfirmation && error) {
      if (isSsnError(error)) {
        setValues({ ...values, validationErrors: { ssnError: 'error' } })
      }
      if (isInvalidAddress(error)) {
        setValues({ ...values, validationErrors: { addressError: 'error' } })
      }
    }
  }, [error])

  return (
    <>
      <TextField
        label="Legal Name"
        id="legal-name"
        variant="standard"
        disabled
        fullWidth
        value={values.legalName}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => navigateStep(GET_PHYSICAL.step)}>
                <EditIcon />
              </IconButton>
            </InputAdornment>
          )
        }}
      />

      <div style={{ display: 'flex', flexFlow: 'row wrap', gap: 25 }}>
        <TextField
          label="Email"
          id="email"
          variant="standard"
          fullWidth
          disabled
          style={{ flex: 2, minWidth: 215 }}
          value={values.email}
        />

        <TextField
          label="Promo Code"
          id="promocode"
          variant="standard"
          fullWidth
          disabled
          style={{ flex: 1, minWidth: 150 }}
          value={values.promoCode}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => navigateStep(ENTER_PROMO.step)}>
                  <EditIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </div>

      <div style={{ display: 'flex', flexFlow: 'row wrap', gap: 25 }}>
        <TextField
          label="Mobile Number"
          id="primaryPhone"
          variant="standard"
          fullWidth
          disabled
          value={values.primaryPhone}
          style={{ flex: 1, minWidth: 150 }}
        />

        <TextField
          label="Total Annual Income"
          id="annualIncome"
          variant="standard"
          fullWidth
          disabled
          style={{ flex: 1, minWidth: 150 }}
          value={values.annualIncome}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => navigateStep(SAFE_AND_SECURE.step)}>
                  <EditIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </div>

      <TextField
        label="Address"
        id="address"
        variant="standard"
        fullWidth
        multiline
        rows={2}
        disabled
        error={!!values.validationErrors.addressError}
        helperText={
          values.validationErrors.addressError ? 'Invalid Address' : ''
        }
        value={values.address}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => navigateStep(GET_PHYSICAL.step)}>
                <EditIcon />
              </IconButton>
            </InputAdornment>
          )
        }}
      />

      <div
        style={{
          display: 'flex',
          flexFlow: 'row wrap',
          gap: 25,
          marginBottom: 20
        }}
      >
        <TextField
          label="Birth date"
          id="dob"
          variant="standard"
          fullWidth
          disabled
          style={{ flex: 1, minWidth: 150 }}
          value={values.dob}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => navigateStep(SAFE_AND_SECURE.step)}>
                  <EditIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        <TextField
          label="Social Security Number"
          id="ssn"
          variant="standard"
          fullWidth
          disabled
          error={!!values.validationErrors.ssnError}
          helperText={
            values.validationErrors.ssnError ? 'SSN is not valid' : ''
          }
          style={{ flex: 1, minWidth: 150 }}
          value={values.ssn}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => navigateStep(SAFE_AND_SECURE.step)}>
                  <EditIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </div>

      <Typography component="div" variant="caption">
        The following information will be used to verify your identity and
        pre-approve your credit card application. Please read the terms before
        applying. By selecting Agree, you agree to these terms and conditions as
        well as apply.
      </Typography>

      <div style={{ display: 'flex', flexFlow: 'row nowrap', gap: 25 }}>
        <Checkbox
          checked={values.kycConfirmation}
          onChange={(e) =>
            setValues({ ...values, kycConfirmation: e.target.checked })
          }
          inputProps={{ 'aria-label': 'controlled' }}
        />
        <Typography component="div" variant="caption">
          I confirm that I have read and agreed to the{' '}
          <a
            href="https://paceline.fit/legal/application-terms-and-conditions"
            target="_blank"
          >
            Application Terms and Conditions, Privacy Notice,
          </a>{' '}
          and{' '}
          <a
            href="https://dq1xxrax08ia8.cloudfront.net/card/legal/Electronic_Communication_Consent-Ver.3.0.pdf"
            target="_blank"
          >
            Electronic Communication Agreement.
          </a>
        </Typography>
      </div>

      <Typography component="div" variant="caption">
        By tapping or clicking the "Apply" button, I acknowledge that I am
        providing written instructions under the Fair Credit Reporting Act
        authorizing Paceline and its affiliated partners to obtain my credit
        report from one or more of the credit reporting agencies. To check the
        rates you may be pre-approved for, we will perform a soft credit inquiry
        that will not impact your credit score.
      </Typography>
    </>
  )
}
